import { virtualDir } from "#skyward_global";
import { DEFAULT_CLOSE_BUTTON, showDialog } from "#skyward_modalLayer";
import { insertTextToken } from "./skyward.base.global.text";
import { openBindingDialog } from "./skyward.ui.binding";
import { getValue, setValue, toggleVisible } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";
import { JQueryInput } from "./types";

const objectsThatRequireShownProxies = ["TimeOffTransactionRequest"];

const classes =
{
    wrapper: "messageCenterWrapper",
    messageCenterMode: "MessageCenterMode",
    messageSubjectAndContent: "messageSubjectAndContent",
    messageSubject: "messageMasterSubject",
    messageContent: "messageMasterContent",
    messageFromAlias: "messageMasterFromAlias",
    messageAllowReplies: "messageMasterAllowReplies",
    messageType: "messageType",
    notificationSubType: "notificationSubType",
    includeMessageBool: "includeMessageBool",
    insertPrimaryBindingButton: "insertPrimaryBindingButton",
    toWhomInput: "jsMessageCenterToWhomCode",
    standardFilterViewer: "js-standardFilterViewer",
    filterViewer: "js-filterViewer",
    relationshipConditionStandardFilterViewer: "js-relationshipConditionStandardFilterViewer",
    dataObjectViewer: "dataObjectViewer",
    workflow: "workflow",
    skywardContentPanel: "skywardContentPanel"
};

let subjectIsSelected: boolean;

const events =
{
    init,
    displayMessagePreview
};

// MessageMaster.ToWhoms
const toWhoms = Object.freeze(
    {
        blank: "B",
        guardians: "G",
        students: "S",
        staff: "T",
        employee: "E",
        users: "U",
        applicants: "A",
        activityPersonnel: "P"
    });

// Controls which insert fields appear for the respective message recipient type for Activity and Teacher access.
// The Map keys are the recipients. The Sets are the insert field types to show.
const allowedInsertFields = new Map([
    [toWhoms.blank, new Set(toWhoms.blank)],
    [toWhoms.students, new Set(toWhoms.students)],
    [toWhoms.guardians, new Set([toWhoms.students, toWhoms.guardians])],
    [toWhoms.activityPersonnel, new Set([toWhoms.activityPersonnel])]
]);

registerEvents("messagecenter", events);

export function resetTemplate(button: JQueryInput, defaultSubject: string, defaultContent: string)
{
    const container = $(button).closest(`.${classes.wrapper}`);
    setValue(container.find(`.${classes.messageSubject}`), defaultSubject, false);
    setValue(container.find(`.${classes.messageContent}`), defaultContent, false);
}

function init(this: HTMLElement)
{
    const messageCenterWrapper = $(this);

    messageCenterWrapper.find(`.${classes.messageSubject}`).on("click focus", function () { subjectIsSelected = true; });
    messageCenterWrapper.find(`.${classes.messageContent}`).on("click focus", function () { subjectIsSelected = false; });
    SetMessageBindingOptions(messageCenterWrapper);

    if (messageCenterWrapper.find(`.${classes.messageSubjectAndContent}`).length === 0)
    {
        messageCenterWrapper.each(function () { initOldSxlControls($(this)); });
    }
}

// TODO: screens that rely on initOldSxlControls should probably call initOldSxlControls instead of calling init directly, or better yet, just updated to use the new MessageSubjectAndContent viewpart
function initOldSxlControls(messageCenterWrapper: JQuery<HTMLElement>)
{
    const messageCenterObject = messageCenterWrapper.find(`.${classes.messageCenterMode}`);
    const renderProxies = objectsThatRequireShownProxies.indexOf(getValue(messageCenterObject)) !== -1;

    messageCenterWrapper.find(`.${classes.toWhomInput}`).on("change.recipient", function ()
    {
        const toWhomComboBox = $(this);
        if (!toWhomComboBox.attr("aria-readonly"))
        {
            resetMessageToWhom(messageCenterWrapper);
        }
        SetMessageBindingOptions(messageCenterWrapper);
    });
    messageCenterWrapper.find(`.${classes.insertPrimaryBindingButton}`).on("click.insertBinding", function ()
    {
        let selectedToWhomOption = messageCenterWrapper.find(`.${classes.toWhomInput}`).find("option:selected");
        if (selectedToWhomOption.length === 0)
        {
            selectedToWhomOption = messageCenterWrapper;
        }
        const toWhomData = selectedToWhomOption.data();
        openInsertBindingView(messageCenterWrapper, toWhomData.module, toWhomData.object, $(this), renderProxies);
    });
}

function resetMessageToWhom(container: JQuery<HTMLElement>)
{
    //Clear Subject and Content
    setValue(container.find(`.${classes.messageSubject}`), "");
    setValue(container.find(`.${classes.messageContent}`), "");
}

function openInsertBindingView(container: JQuery<HTMLElement>, dataModule: string, dataObject: string, anchorToElement: JQuery<HTMLElement>, renderProxies: boolean)
{
    const options =
    {
        anchorTo: anchorToElement,
        renderProxies: renderProxies,
        removeBaseFromBinding: true,
        onGetBinding: function (binding?: string) { insertBinding(container, binding); }
    };

    openBindingDialog(dataModule, dataObject, options);
}

function displayMessagePreview(this: HTMLElement, _event: JQuery.ClickEvent)
{
    const previewButton = $(this);
    const buttonData = previewButton.data();

    const messageCenterWrapper = previewButton.parents(`.${classes.wrapper}`);
    const wrapperData = messageCenterWrapper.data();

    const messageCenterObject = getValue(messageCenterWrapper.find(`.${classes.messageCenterMode}`));

    const subject = getValue(messageCenterWrapper.find(`.${classes.messageSubject}`));
    const content = getValue(messageCenterWrapper.find(`.${classes.messageContent}`));

    const fromAlias = getValue(messageCenterWrapper.find(`.${classes.messageFromAlias}`));
    const recipientTypeValue = getValue(messageCenterWrapper.find(`.${classes.toWhomInput}`));

    let filterWrapper = messageCenterWrapper;

    const filterSelector = wrapperData.filterselector;

    if (filterSelector)
    {
        filterWrapper = $(filterSelector);
    }

    const filter = getValue(filterWrapper.find(`.${classes.standardFilterViewer}, .${classes.filterViewer}`));
    const relationshipconditionfilter = getValue(filterWrapper.find(`.${classes.relationshipConditionStandardFilterViewer} .${classes.standardFilterViewer}`));

    const includeAutoGeneratedMessage = getValue(messageCenterWrapper.find(`.${classes.includeMessageBool}`));

    const allowRepliesInput = messageCenterWrapper.find(`.${classes.messageAllowReplies}`);
    const allowReplies = allowRepliesInput ? getValue(allowRepliesInput) : false;

    const type = getValue(messageCenterWrapper.find(`.${classes.messageType}`));
    const notificationSubType = getValue(messageCenterWrapper.find(`.${classes.notificationSubType}`));

    const workflowInfoInput = this.closest(`.${classes.workflow}.${classes.skywardContentPanel}`);
    const detailsScreenInfoInput = this.closest(`.${classes.dataObjectViewer}`);
    const locationElement = workflowInfoInput ? workflowInfoInput : detailsScreenInfoInput;
    const locationData = locationElement ? $(locationElement).data() : {};

    let actionPath: string;

    const previewActionPopupModulePath = wrapperData.previewactionpopupmodulepath;

    if (buttonData.previewAction)
    {
        actionPath = buttonData.previewAction;
    }
    else if (previewActionPopupModulePath)
    {
        // Used in applicant tracking, this should be removed when it gets updated to use the MessageSubjectAndContent viewpart
        actionPath = previewActionPopupModulePath;
    }
    else
    {
        actionPath = `MessageCenter/MessageMaster/GetMessagePreviewPopup${messageCenterObject}`;
    }

    actionPath = actionPath + '/';

    const buildPopup = $.ajax({
        url: virtualDir + actionPath,
        type: "POST",
        dataType: "viewparthtml",
        data:
        {
            subject: subject,
            content: content,
            recipientType: recipientTypeValue,
            filter: filter,
            relationshipconditionfilter: relationshipconditionfilter,
            typeID: type,
            notificationSubType: notificationSubType,
            securityScreen: locationData.screen,
            allowReplies: allowReplies,
            includeAutoGeneratedMessage: includeAutoGeneratedMessage,
            fromAlias: fromAlias,
            extrafilterjson: JSON.stringify(wrapperData.extrafilterjson),
            extrafilterjsonhash: wrapperData.extrafilterjsonhash
        }
    });

    showDialog("Preview Message", buildPopup, [DEFAULT_CLOSE_BUTTON], { anchorTo: this, automationID: "message-preview-dialog" });
}

function SetMessageBindingOptions(container: JQuery<HTMLElement>)
{
    const comboBox = container.find("[id$=MessageBindingFields]");
    if (comboBox.length > 0)
    {
        const insertFields = comboBox.find("option");
        const toWhomCode = getValue(container.find(`.${classes.toWhomInput}`)) || toWhoms.blank;
        const allowedInsertFieldTypes = allowedInsertFields.get(toWhomCode);

        insertFields.each((_i, insertField) =>
        {
            const insertFieldType = insertField.dataset.type || toWhoms.blank;
            const isVisible = allowedInsertFieldTypes?.has(insertFieldType) || false;

            toggleVisible(insertField, isVisible);
        });
    }
}

function insertBinding(container: JQuery<HTMLElement>, binding?: string)
{
    let controlToUpdate;
    if (subjectIsSelected == true || subjectIsSelected == undefined)
    {
        controlToUpdate = container.find(`.${classes.messageSubject}`);
    }
    else
    {
        controlToUpdate = container.find(`.${classes.messageContent}`);
    }

    insertTextToken(controlToUpdate as JQuery<HTMLInputElement>, binding, true);
}